import React, { useState, useMemo, useEffect } from "react";
import { useToast } from "./Toast";
import {
  MONTHS_T,
  BOOKED,
  FEATURED,
  TEMAS,
  PROXIMOS,
} from "../data/content";
import { isValidName, isValidPhone, isValidEmail, sanitizePhone } from "../utils/validation";

const key = (y, m) => `${y}-${m}`;

function validateReserva({ selected, nombre, telefono, correo, adultos }) {
  const errors = {};
  if (!selected) {
    errors.dia = "Elige un día en el calendario";
  }
  if (!isValidName(nombre)) {
    errors.nombre = "Escribe tu nombre completo";
  }
  if (!isValidPhone(telefono)) {
    errors.telefono = "Escribe solo números (10 a 15 dígitos)";
  }
  if (!isValidEmail(correo)) {
    errors.correo = "Escribe un correo válido";
  }
  if (!adultos || Number(adultos) < 1) {
    errors.adultos = "Indica cuántos adultos asistirán";
  }
  return errors;
}

export default function Calendario() {
  const toast = useToast();
  const today = useMemo(() => new Date(), []);

  const [view, setView] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [selected, setSelected] = useState(null);
  const [adultos, setAdultos] = useState("");
  const [ninos, setNinos] = useState("");
  const [tema, setTema] = useState(TEMAS[0].key);
  const [nombre, setNombre] = useState("");
  const [telefono, setTelefono] = useState("");
  const [correo, setCorreo] = useState("");
  const [sending, setSending] = useState(false);
  const [touched, setTouched] = useState({});
  const [diaTouched, setDiaTouched] = useState(false);

  // ---- disponibilidad real (Azure SQL); arranca con el mock y se sobrescribe si la API responde ----
  const [booked, setBooked] = useState(BOOKED);
  const [featured, setFeatured] = useState(FEATURED);
  const [featuredNotes, setFeaturedNotes] = useState({});

  useEffect(() => {
    let cancelled = false;
    fetch("/api/calendar")
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        if (cancelled) return;
        if (data.booked) setBooked(data.booked);
        if (data.featured) setFeatured(data.featured);
        if (data.featuredNotes) setFeaturedNotes(data.featuredNotes);
      })
      .catch(() => {
        // sin conexión a la API/BD todavía: seguimos mostrando el mock
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- construir matriz de días ----
  const cells = useMemo(() => {
    const { y, m } = view;
    const first = new Date(y, m, 1).getDay();
    const dim = new Date(y, m + 1, 0).getDate();
    const bset = new Set(booked[key(y, m)] || []);
    const fset = new Set(featured[key(y, m)] || []);
    const fnotes = featuredNotes[key(y, m)] || {};
    const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const out = [];
    for (let i = 0; i < first; i++) out.push({ empty: true, id: "e" + i });
    for (let d = 1; d <= dim; d++) {
      const dow = new Date(y, m, d).getDay();
      const cur = new Date(y, m, d);
      out.push({
        d,
        id: "d" + d,
        weekend: dow === 0 || dow === 6,
        past: cur < todayMid,
        today:
          y === today.getFullYear() && m === today.getMonth() && d === today.getDate(),
        booked: bset.has(d),
        featured: fset.has(d),
        nota: fnotes[d] || "",
      });
    }
    return out;
  }, [view, today, booked, featured, featuredNotes]);

  const isCurrentMonth = view.y === today.getFullYear() && view.m === today.getMonth();

  const prevMo = () =>
    setView((v) => {
      if (v.y === today.getFullYear() && v.m === today.getMonth()) return v;
      return v.m === 0 ? { y: v.y - 1, m: 11 } : { ...v, m: v.m - 1 };
    });
  const nextMo = () =>
    setView((v) => (v.m === 11 ? { y: v.y + 1, m: 0 } : { ...v, m: v.m + 1 }));

  const pick = (c) => {
    if (c.empty || c.past || c.booked) return;
    setSelected({ d: c.d, m: view.m, y: view.y });
    setDiaTouched(true);
  };

  const onTelefonoChange = (e) => {
    setTelefono(sanitizePhone(e.target.value));
    setTouched((prev) => ({ ...prev, telefono: true }));
  };

  const errors = useMemo(
    () => validateReserva({ selected, nombre, telefono, correo, adultos }),
    [selected, nombre, telefono, correo, adultos]
  );
  const canSubmit = Object.keys(errors).length === 0;
  const showFieldError = (name) => touched[name] && errors[name];

  const reservar = async () => {
    if (!canSubmit) {
      setTouched({ nombre: true, telefono: true, correo: true, adultos: true });
      setDiaTouched(true);
      toast("Revisa los campos marcados en rojo ✿");
      return;
    }
    const { d, m, y } = selected;
    const fechaTentativa = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    setSending(true);
    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          origen: "cotizacion-calendario",
          nombre,
          telefono,
          correo,
          adultos,
          ninos: ninos ? Number(ninos) : 0,
          fechaTentativa,
          tematica: tema,
        }),
      });
      if (!res.ok) throw new Error("bad status");
      toast(
        `✿ ¡Solicitud enviada! Te contactamos para armar tu cotización del ${String(d).padStart(
          2,
          "0"
        )}/${String(m + 1).padStart(2, "0")}/${y} ✿`
      );
      setNombre("");
      setTelefono("");
      setCorreo("");
      setAdultos("");
      setNinos("");
      setTouched({});
      setDiaTouched(false);
      setSelected(null);
    } catch {
      toast("No pudimos enviar tu solicitud, intenta de nuevo en unos minutos ✿");
    } finally {
      setSending(false);
    }
  };

  const selLabel = selected
    ? `✿ ${String(selected.d).padStart(2, "0")} de ${MONTHS_T[
        selected.m
      ].toLowerCase()} de ${selected.y} ✿`
    : "— elige un día en el calendario —";

  return (
    <section id="calendario" className="band coral" data-screen-label="04 Calendario">
      <div className="band-bg" />
      <div className="scatter" />
      <div className="tear-top tear-coral-top" />
      <div className="tear-bottom tear-coral-bottom" />

      <div className="wrap">
        <div className="sec-head">
          <h2 className="sec-title">Calendarización</h2>
        </div>

        <div className="cal-shell">
          {/* ---- Calendario ---- */}
          <div className="cal-card">
            <div className="cal-head">
              <div className="month">
                {MONTHS_T[view.m]}
                <small>· {view.y}</small>
              </div>
              <div className="cal-nav">
                <button onClick={prevMo} aria-label="Mes anterior" disabled={isCurrentMonth}>‹</button>
                <button onClick={nextMo} aria-label="Mes siguiente">›</button>
              </div>
            </div>
            <div className="dow">
              {["DOM", "LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB"].map((d) => (
                <span key={d}>{d}</span>
              ))}
            </div>
            <div className="days">
              {cells.map((c) => {
                if (c.empty) return <div className="day empty" key={c.id} />;
                const isSel =
                  selected && selected.d === c.d && selected.m === view.m && selected.y === view.y;
                const cls = [
                  "day",
                  c.weekend && "weekend",
                  c.past && "past",
                  c.today && "today",
                  c.booked && "booked",
                  c.featured && "featured",
                  isSel && "selected",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <div
                    className={cls}
                    key={c.id}
                    onClick={() => pick(c)}
                    title={c.featured && c.nota ? c.nota : undefined}
                  >
                    {c.d}
                    {c.featured && <span className="dot" />}
                  </div>
                );
              })}
            </div>
            <div className="legend">
              <span className="l-free"><i />Disponible</span>
              <span className="l-feat"><i />Promoción</span>
              <span className="l-sel"><i />Seleccionado</span>
              <span className="l-book"><i />Reservado</span>
              <span className="l-past"><i />Pasado</span>
            </div>
          </div>

          {/* ---- Reserva ---- */}
          <aside className="booking">
            <h4>Solicita tu fiesta</h4>
            <div className={"sel-day" + (diaTouched && errors.dia ? " has-error" : "")}>{selLabel}</div>
            {diaTouched && errors.dia && <span className="field-error">{errors.dia}</span>}

            <div className="row2c">
              <div>
                <label className="field">Nombre</label>
                <input
                  className={"input" + (showFieldError("nombre") ? " has-error" : "")}
                  placeholder="María González"
                  value={nombre}
                  onChange={(e) => {
                    setNombre(e.target.value);
                    setTouched((prev) => ({ ...prev, nombre: true }));
                  }}
                />
                {showFieldError("nombre") && <span className="field-error">{errors.nombre}</span>}
              </div>
              <div>
                <label className="field">Teléfono / WhatsApp</label>
                <input
                  className={"input" + (showFieldError("telefono") ? " has-error" : "")}
                  type="tel"
                  inputMode="numeric"
                  placeholder="5610089073"
                  value={telefono}
                  onChange={onTelefonoChange}
                />
                {showFieldError("telefono") && <span className="field-error">{errors.telefono}</span>}
              </div>
            </div>

            <label className="field">Correo</label>
            <input
              className={"input" + (showFieldError("correo") ? " has-error" : "")}
              type="email"
              placeholder="maria@correo.mx"
              value={correo}
              onChange={(e) => {
                setCorreo(e.target.value);
                setTouched((prev) => ({ ...prev, correo: true }));
              }}
            />
            {showFieldError("correo") && <span className="field-error">{errors.correo}</span>}

            <div className="row2c">
              <div>
                <label className="field">Adultos</label>
                <input
                  className={"input" + (showFieldError("adultos") ? " has-error" : "")}
                  type="number"
                  min="0"
                  placeholder="Ej. 20"
                  value={adultos}
                  onChange={(e) => {
                    setAdultos(e.target.value);
                    setTouched((prev) => ({ ...prev, adultos: true }));
                  }}
                />
                {showFieldError("adultos") && <span className="field-error">{errors.adultos}</span>}
              </div>
              <div>
                <label className="field">Niños</label>
                <input
                  className="input"
                  type="number"
                  min="0"
                  placeholder="Ej. 15 (opcional)"
                  value={ninos}
                  onChange={(e) => setNinos(e.target.value)}
                />
              </div>
            </div>

            <label className="field">Temática</label>
            <div className="themes">
              {TEMAS.map((t) => (
                <div
                  key={t.key}
                  className={"theme" + (tema === t.key ? " active" : "")}
                  onClick={() => setTema(t.key)}
                >
                  {t.label}
                </div>
              ))}
            </div>

            <button className="btn" onClick={reservar} disabled={!canSubmit || sending}>
              {sending ? (
                <>
                  <span className="btn-spinner" /> Reservando…
                </>
              ) : (
                "Solicitar cotización"
              )}
            </button>
          </aside>
        </div>

        {/* ---- Próximos eventos ---- */}
        <div className="events">
          {PROXIMOS.map((e, i) => (
            <div className="event" key={i}>
              <div className="date">
                <b>{String(e.d).padStart(2, "0")}</b>
                <span>{MONTHS_T[e.m].toUpperCase().slice(0, 3)}</span>
              </div>
              <div>
                <h5>{e.title}</h5>
                <small>{e.sub}</small>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
